import React, { useState, useEffect } from 'react';
import { X, ArrowLeft, ArrowRight, Check, ChevronRight } from 'lucide-react';
import { useOnboarding } from '../hooks/useOnboarding';
import { OnboardingSteps } from './OnboardingSteps';
import { ProgressIndicator } from './ProgressIndicator';
import Button from '../../../components/base/Button';
import Card from '../../../components/base/Card';
import type { OnboardingStep } from '../../../shared/types/onboarding';

interface OnboardingWizardProps {
  onComplete?: () => void;
  onSkip?: () => void;
  className?: string;
}

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  onComplete,
  onSkip,
  className = ''
}) => {
  const {
    progress,
    config,
    loading,
    completeStep,
    skipStep,
    goToPreviousStep,
    goToNextStep,
    completeOnboarding,
    getCurrentStep,
    isStepCompleted,
    getProgressPercentage,
    canGoBack,
    canSkip
  } = useOnboarding();

  const [steps, setSteps] = useState<OnboardingStep[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Inicializar pasos del onboarding
  useEffect(() => {
    const onboardingSteps = OnboardingSteps.getSteps();
    setSteps(onboardingSteps);
    setCurrentStepIndex(getCurrentStep());
  }, [getCurrentStep]);

  // Mostrar wizard si debe aparecer
  useEffect(() => {
    if (!loading && progress && !progress.isCompleted && config.showWelcome) {
      setIsVisible(true);
    }
  }, [loading, progress, config.showWelcome]);

  // Actualizar índice del paso actual
  useEffect(() => {
    const currentStep = getCurrentStep();
    if (currentStep !== currentStepIndex) {
      setCurrentStepIndex(currentStep);
    }
  }, [getCurrentStep, currentStepIndex]);

  const handleNext = async () => {
    const currentStep = steps[currentStepIndex];
    if (!currentStep) return;

    // Completar el paso actual
    const success = await completeStep(currentStep.id);
    if (success) {
      if (currentStepIndex < steps.length - 1) {
        goToNextStep();
      } else {
        // Último paso - completar onboarding
        await handleComplete();
      }
    }
  };

  const handlePrevious = () => {
    if (canGoBack) {
      goToPreviousStep();
    }
  };

  const handleSkipStep = async () => {
    const currentStep = steps[currentStepIndex];
    if (!currentStep || !canSkip) return;

    const success = await skipStep(currentStep.id);
    if (success) {
      if (currentStepIndex < steps.length - 1) {
        goToNextStep();
      } else {
        await handleComplete();
      }
    }
  };

  const handleComplete = async () => {
    const success = await completeOnboarding();
    if (success) {
      setIsVisible(false);
      onComplete?.();
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    onSkip?.();
  };

  if (loading || !isVisible || steps.length === 0) {
    return null;
  }

  const currentStep = steps[currentStepIndex];
  const progressPercentage = getProgressPercentage(steps.length);
  const isLastStep = currentStepIndex === steps.length - 1;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Bienvenido a Invirtiendo
            </h2>
            <ProgressIndicator
              current={currentStepIndex + 1}
              total={steps.length}
              percentage={progressPercentage}
            />
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Contenido del paso actual */}
        <div className="p-6">
          {currentStep && (
            <div className="space-y-6">
              {/* Título y descripción */}
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">{currentStep.icon}</span>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {currentStep.title}
                </h3>
                <p className="text-gray-600 text-lg">
                  {currentStep.description}
                </p>
              </div>

              {/* Contenido específico del paso */}
              <div className="bg-gray-50 rounded-lg p-6">
                {currentStep.content}
              </div>
            </div>
          )}
        </div>

        {/* Footer con navegación */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            {/* Botón Anterior */}
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={!canGoBack}
              className="flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Anterior
            </Button>

            {/* Botón Saltar (si está permitido) */}
            {canSkip && !isLastStep && (
              <Button
                variant="ghost"
                onClick={handleSkipStep}
                className="flex items-center text-gray-600"
              >
                <ChevronRight className="w-4 h-4 mr-2" />
                Saltar
              </Button>
            )}
          </div>

          <div className="flex items-center space-x-3">
            {/* Indicador de paso */}
            <span className="text-sm text-gray-500">
              {currentStepIndex + 1} de {steps.length}
            </span>

            {/* Botón Siguiente/Completar */}
            <Button
              onClick={isLastStep ? handleComplete : handleNext}
              className="flex items-center bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isLastStep ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Completar
                </>
              ) : (
                <>
                  Siguiente
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};